/**
 * Trauma screen shake (after Ghost Circuit's viewShake): hits add trauma (0–1), it decays
 * linearly, and the shake is trauma² so small knocks barely move while big ones really jolt.
 * The wobble is a sum of two sines per axis at unrelated rates: lively, never a regular buzz,
 * and smooth at any frame rate (no per-frame random jitter). Small, frequent hits (merges,
 * landings) use `kick` instead: a directional punch felt from the first frame, gone in ~0.12 s.
 *
 * Pure state, no rendering: the renderer applies `offset()` to the world container (Pixi) or
 * a ctx.translate (Canvas 2D). It never touches the DOM or the game's input mapping.
 */

/** Ghost Circuit's wobble: two sines, `k` decorrelates the axes. */
export function wobble(t, k) {
  return Math.sin(t * (13.1 + k)) * 0.6 + Math.sin(t * (31.7 + k * 2.3)) * 0.4;
}

/** Kick envelope: full at once, gone in ~0.12 s with one small rebound (a punch, not a wobble). */
var KICK_TAU = 0.04;
var KICK_OMEGA = 44;
var KICK_LIFE = 0.16;
export function kickEnvelope(age) {
  if (!(age >= 0) || age >= KICK_LIFE) return 0;
  return Math.exp(-age / KICK_TAU) * Math.cos(age * KICK_OMEGA);
}

/**
 * @param {{ decay?: number, maxX?: number, maxY?: number, maxAngle?: number, rate?: number, maxKick?: number }} [opts]
 *   decay: trauma lost per second; maxX/maxY: px at trauma 1; maxAngle: radians at trauma 1;
 *   rate: wobble speed multiplier (1 = Ghost Circuit's ~2–5 Hz); maxKick: largest kick (px).
 */
export function createTraumaShake(opts) {
  var o = opts || {};
  var state = {
    trauma: 0,
    decay: o.decay != null ? o.decay : 1.5,
    maxX: o.maxX != null ? o.maxX : 14,
    maxY: o.maxY != null ? o.maxY : 12,
    maxAngle: o.maxAngle != null ? o.maxAngle : 0,
    rate: o.rate != null ? o.rate : 1,
    maxKick: o.maxKick != null ? o.maxKick : (o.maxX != null ? o.maxX : 14) * 0.5,
  };
  // The directional impulse: amplitude vector (px) and its age (s). A fresh kick skips the next
  // update, so the frame drawn right after the hit shows it at full strength whatever the dt.
  var kick = { x: 0, y: 0, age: KICK_LIFE, fresh: false };

  return {
    /** Add trauma (clamped to 1). */
    add: function (amount) {
      var a = Number(amount);
      if (!(a > 0)) return;
      state.trauma = Math.min(1, state.trauma + a);
    },
    /** Raise trauma to at least `amount` (a big hit should not stack past a cap). */
    atLeast: function (amount) {
      var a = Number(amount);
      if (a > state.trauma) state.trauma = Math.min(1, a);
    },
    /**
     * A directional kick: the view jumps `px` along `angle` (radians, 0 = right, π/2 = down) at
     * once and springs back within ~0.12 s. Felt on the very first frame, which a small trauma
     * (shake = trauma²) never is. A kick landing on a live one adds to what is left of it.
     */
    kick: function (px, angle) {
      var p = Number(px);
      if (!(p > 0)) return;
      var a = Number(angle) || 0;
      var left = kickEnvelope(kick.age);
      var nx = kick.x * left + Math.cos(a) * p;
      var ny = kick.y * left + Math.sin(a) * p;
      var m = Math.sqrt(nx * nx + ny * ny);
      if (m > state.maxKick) {
        nx *= state.maxKick / m;
        ny *= state.maxKick / m;
      }
      kick.x = nx;
      kick.y = ny;
      kick.age = 0;
      kick.fresh = true;
    },
    update: function (dt) {
      if (!(dt > 0)) return;
      if (kick.fresh) kick.fresh = false;
      else if (kick.age < KICK_LIFE) kick.age += dt;
      if (state.trauma <= 0) return;
      state.trauma = Math.max(0, state.trauma - state.decay * dt);
    },
    clear: function () {
      state.trauma = 0;
      kick.age = KICK_LIFE;
      kick.fresh = false;
    },
    get trauma() {
      return state.trauma;
    },
    /** shake = trauma²: the felt amount (0–1). */
    get amount() {
      return state.trauma * state.trauma;
    },
    /**
     * Offset at time `t` (seconds), scaled by `scale` (px multiplier for the layout).
     * @returns {{ x: number, y: number, angle: number }}
     */
    offset: function (t, scale) {
      var s = state.trauma * state.trauma;
      var ke = kickEnvelope(kick.age);
      if (s <= 0.0001 && ke === 0) return { x: 0, y: 0, angle: 0 };
      var k = scale != null ? scale : 1;
      if (s <= 0.0001) return { x: kick.x * ke * k, y: kick.y * ke * k, angle: 0 };
      var tt = t * state.rate;
      return {
        x: (s * state.maxX * wobble(tt, 0) + kick.x * ke) * k,
        y: (s * state.maxY * wobble(tt, 7) + kick.y * ke) * k,
        angle: state.maxAngle ? s * state.maxAngle * wobble(tt, 3) : 0,
      };
    },
  };
}
